// Batch Price Dialog - Adjust prices for multiple products at once
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { DollarSign, Percent, Calculator } from 'lucide-react';

type AdjustmentMode = 'fixed' | 'percent' | 'amount';

interface BatchPriceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (mode: AdjustmentMode, value: number) => void;
  productCount: number;
  scope: 'selected' | 'section' | 'all';
}

export function BatchPriceDialog({
  isOpen,
  onClose,
  onApply,
  productCount,
  scope,
}: BatchPriceDialogProps) {
  const [mode, setMode] = useState<AdjustmentMode>('percent');
  const [value, setValue] = useState('');

  const handleApply = () => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    onApply(mode, numValue);
    setValue('');
    onClose();
  };

  const getScopeLabel = () => {
    switch (scope) {
      case 'selected': return `${productCount} selected products`;
      case 'section': return `all products in section (${productCount})`;
      case 'all': return `all products in flyer (${productCount})`;
    }
  };

  const getPreview = () => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;

    const examplePrice = 9.99;
    let newPrice: number;

    switch (mode) {
      case 'fixed':
        newPrice = numValue;
        break;
      case 'percent':
        newPrice = examplePrice * (1 + numValue / 100);
        break;
      case 'amount':
        newPrice = examplePrice + numValue;
        break;
    }

    return (
      <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
        <span className="font-medium">Preview:</span> $9.99 → ${Math.max(0, newPrice).toFixed(2)}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Batch Price Adjustment
          </DialogTitle>
          <DialogDescription>
            Adjust prices for {getScopeLabel()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as AdjustmentMode)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <RadioGroupItem value="fixed" id="fixed" />
              <Label htmlFor="fixed" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="font-medium">Set Fixed Price</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Set all products to a specific price</p>
              </Label>
            </div>

            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <RadioGroupItem value="percent" id="percent" />
              <Label htmlFor="percent" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">Adjust by Percentage</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Use negative values for discounts (e.g., -10)</p>
              </Label>
            </div>

            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <RadioGroupItem value="amount" id="amount" />
              <Label htmlFor="amount" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">Adjust by Amount</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Add or subtract a fixed dollar amount</p>
              </Label>
            </div>
          </RadioGroup>

          <div>
            <Label htmlFor="value">
              {mode === 'fixed' ? 'New Price ($)' :
               mode === 'percent' ? 'Percentage (%)' :
               'Amount ($)'}
            </Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                {mode === 'percent' ? '%' : '$'}
              </span>
              <Input
                id="value"
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="pl-8"
                placeholder={mode === 'percent' ? '-10 or 15' : '0.00'}
                autoFocus
              />
            </div>
            {getPreview()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!value || isNaN(parseFloat(value))}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Apply to {productCount} Products
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
